import WeatherFlow from "@/components/game/WeatherFlow";
import SeaRiseSummary from "@/components/game/SeaRiseSummary";
import { FaceChip, WeatherLegend } from "@/components/game/WeatherDice";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PLAYER_COLORS, PLAYER_ON_COLOR } from "@/lib/api";
import { globalEffectText } from "@/components/game/WeatherCardReveal";

// LAYOUT F — the fixed weather panel is gone:
//   WeatherChip (top bar): "Weather: Heavy Rain · round 3" / "Weather: none"; a click opens the
//                          weather deck (cards per face, cards left, what left the deck at each
//                          degree), the last global card and the last results.
//   EventPanel (left column, in the SELECTED CARD area): the sea rise summary, or the weather
//                          results / Protection prompt / "Next round in 5…" while the round ends.

const POP_STYLE = { background: "var(--card)", borderColor: "var(--line)", boxShadow: "var(--shadow-card)" };
export const deckLeft = (game) => game.weather_deck_size ?? (game.weather_deck || []).length;
export const globalLeft = (game) => game.global_deck_size ?? (game.global_deck || []).length;
const removedText = (removed, faces) => {
  const names = Object.fromEntries((faces || []).map((f) => [f.key, f.name]));
  const parts = Object.entries(removed || {}).filter(([, n]) => n).map(([k, n]) => `${n} ${names[k] || k}`);
  return parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0] || "no cards";
};
// What left the deck so far, one line per degree (the pile tooltip + the deck popover).
export const removalLines = (game, faces) =>
  (game.weather_removed || []).map((r) => `At ${r.degree}°, ${removedText(r.removed, faces?.faces)} left the deck.`);
const strip = (t) => (t || "").replace(/\s*\(\d+ became debt\)\.?$/, "");

export function lastWeather(game, faces) {
  const list = faces?.faces || [];
  const name = game.last_weather === "Sunny Day" ? "Clear Skies" : game.last_weather === "Extreme Heat Wave" ? "Heat Storm" : game.last_weather;
  const face = name ? list.find((f) => f.name === name) || null : null;
  const log = game.weather_log || [];
  const round = log.length ? log[log.length - 1].round : null;
  return { name, face, round, list };
}

export function WeatherChip({ game, faces }) {
  const { name, face, round, list } = lastWeather(game, faces);
  const results = game.water_summary?.results || {};
  const hit = game.players.map((p, i) => ({ p, i, text: results[p.color] })).filter((x) => x.text);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="weather-chip"
          data-face={face?.key || ""}
          className="shrink-0 inline-flex items-center whitespace-nowrap list-row-lift"
          style={{ height: 38, gap: 7, padding: "0 10px 0 6px", borderRadius: 10, background: "var(--card)", border: "1px solid var(--line)", fontSize: 12.5, color: "var(--ink-2)" }}
          title="The weather deck: cards, what is left and what the warming took out"
        >
          {face ? <FaceChip faceKey={face.key} size={26} radius={6} /> : <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 6, border: "1.5px dashed var(--line)" }} />}
          <span className="flex flex-col items-start" style={{ lineHeight: "13px" }}>
            <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)" }}>Weather</span>
            <span data-testid="weather-chip-text" style={{ fontWeight: 700, color: "var(--ink)" }}>
              {name ? `${name}${round ? ` · round ${round}` : ""}` : "none"}
            </span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[300px] p-3" style={POP_STYLE} data-testid="weather-chip-popover" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="section-label mb-1.5">The weather deck</div>
        <div className="mb-2" style={{ fontSize: 12, lineHeight: "16px", color: "var(--ink-3)" }} data-testid="weather-deck-text">
          One card is drawn at the end of every round and goes to the discard pile; when the deck is empty the discard is shuffled into a new deck.
          Cards left in the deck: <b data-testid="weather-chip-deck-left" style={{ color: "var(--ink)" }}>{deckLeft(game)}</b> (discard {(game.weather_discard || []).length}).
        </div>
        <WeatherLegend faces={list} lit={face?.key || null} removed={game.weather_removed} />
        {(game.weather_removed || []).length > 0 && (
          <div data-testid="weather-removed" className="mt-2" style={{ fontSize: 11.5, lineHeight: "15px", color: "var(--ink-3)" }}>
            {game.weather_removed.map((r) => (
              <div key={r.degree}>{`At ${r.degree}°, ${removedText(r.removed, list)} left the deck.`}</div>
            ))}
          </div>
        )}
        <div className="section-label mt-2.5 mb-1">Global card</div>
        <div data-testid="weather-chip-global" style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
          {game.last_global?.name ? (
            <>
              <b style={{ color: "var(--ink)" }}>{game.last_global.name}</b> {globalEffectText(game.last_global)}
              {game.last_global.by_reserve ? ` (the shared reserve cancelled ${game.last_global.by_reserve})` : ""}
            </>
          ) : "none yet"}
          <span style={{ color: "var(--ink-4)" }}> · {globalLeft(game)} left in the global deck</span>
        </div>
        {name && (
          <div data-testid="weather-last-results" className="mt-2.5 flex flex-col" style={{ gap: 3 }}>
            <div className="section-label">{`Last: ${name}${round ? ` · round ${round}` : ""}`}</div>
            {face?.kind === "none" ? (
              <div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>Nothing happened.</div>
            ) : hit.length === 0 ? (
              <div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>Nobody had a square in the area.</div>
            ) : (
              hit.map(({ p, i, text }) => (
                <div key={p.color} data-testid={`weather-last-result-${p.color}`} className="flex items-start gap-1.5" style={{ fontSize: 12, lineHeight: "15px", color: "var(--ink-2)" }}>
                  <span className="shrink-0 inline-flex items-center justify-center" style={{ width: 15, height: 15, borderRadius: 4, background: PLAYER_COLORS[p.color], color: PLAYER_ON_COLOR[p.color], fontSize: 9.5, fontWeight: 700 }}>
                    {i + 1}
                  </span>
                  <span className="min-w-0">
                    <span style={{ fontWeight: 600, color: "var(--ink)" }}>{p.name}:</span> {strip(text)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// The event shown in the SELECTED CARD area (null when there is none).
export function eventMode(game, seaSummary) {
  if (seaSummary) return "sea_rise";
  if (game.phase === "weather" && game.mode === "main") return "weather";
  return null;
}

export default function EventPanel({ game, faces, seaSummary = null, maxSlr = 3, onCloseSeaSummary, flow, style }) {
  const mode = eventMode(game, seaSummary);
  if (!mode) return null;
  return (
    <section
      data-testid="weather-section"
      data-mode={mode === "sea_rise" ? "sea_rise" : "event"}
      aria-label={mode === "sea_rise" ? "Sea level rise" : "Weather"}
      className="focus-region flex-1 min-h-0 flex flex-col overflow-hidden"
      style={{ borderRadius: "var(--radius)", background: "var(--card)", border: "1px solid var(--sea)", boxShadow: "0 0 0 1px var(--sea)", ...style }}
    >
      <div className="shrink-0 flex items-center" style={{ padding: "8px 12px 0", height: 26 }}>
        <span className="section-label">{mode === "sea_rise" ? "Sea level rise" : "Weather"}</span>
        <span className="ml-auto" style={{ fontSize: 11, fontWeight: 600, color: "var(--sea)" }}>end of round</span>
      </div>
      <div data-testid="weather-scroll" className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ padding: "6px 12px 10px" }}>
        {mode === "sea_rise" ? (
          <SeaRiseSummary summary={seaSummary} players={game.players} maxSlr={maxSlr} onContinue={onCloseSeaSummary} gridRows={game.grid_rows} />
        ) : (
          <WeatherFlow game={game} faces={faces} {...flow} />
        )}
      </div>
    </section>
  );
}
